import type { Permission } from '@edms/shared';
import { ROLE_LABELS } from '@edms/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from './lib/api';
import { useAuth } from './lib/auth';
import { onServiceWorkerMessage, setAppBadge, syncPush } from './lib/push';
import { Icon, type IconName } from './lib/icons';
import { useInboxCounts } from './lib/tasks-ui';
import { TopBarSlot } from './components/top-bar';
import { Avatar, useTheme } from './lib/ui';

export interface NavItem {
  to: string;
  label: string;
  /** Подпись в нижней панели на телефоне. */
  short?: string;
  icon: IconName;
  permission?: Permission;
  soon?: boolean;
  /** Показывать счётчик «ждут меня». */
  counter?: boolean;
}

const MAIN: NavItem[] = [
  { to: '/', label: 'Главная', icon: 'home' },
  { to: '/inbox', label: 'Ждут меня', short: 'Ждут', icon: 'inbox', counter: true },
  { to: '/tasks', label: 'Задачи', icon: 'tasks' },
  { to: '/trips', label: 'Командировки', short: 'Поездки', icon: 'plane' },
  { to: '/regulations', label: 'Автозадачи', short: 'Авто', icon: 'calendar' },
  { to: '/guides', label: 'Инструкции', short: 'Справка', icon: 'book' },
];

export const ADMIN: NavItem[] = [
  { to: '/reports', label: 'Отчёты', icon: 'chart', permission: 'reports.view' },
  { to: '/users', label: 'Сотрудники', icon: 'users', permission: 'users.manage' },
  { to: '/departments', label: 'Отделы', icon: 'building', permission: 'users.manage' },
  { to: '/substitutions', label: 'Замещения', icon: 'swap', permission: 'users.manage' },
  { to: '/routes', label: 'Маршруты', icon: 'route', permission: 'routes.manage' },
  { to: '/approver-groups', label: 'Группы согласующих', icon: 'users', permission: 'routes.manage' },
  { to: '/request-types', label: 'Типы заявок', icon: 'layers', permission: 'request_types.manage' },
  { to: '/templates', label: 'Шаблоны', icon: 'file', permission: 'templates.manage' },
  { to: '/news', label: 'Новости', icon: 'news', permission: 'news.manage' },
  { to: '/broadcast', label: 'Рассылка', icon: 'megaphone', permission: 'notifications.broadcast' },
  { to: '/audit', label: 'Журнал действий', icon: 'log', permission: 'audit.read' },
  { to: '/email-log', label: 'Журнал писем', icon: 'mail', permission: 'email_log.read' },
  { to: '/settings', label: 'Настройки', icon: 'gear', permission: 'settings.manage' },
];

function NavItems({ items }: { items: NavItem[] }) {
  const counts = useInboxCounts();
  const waiting = counts.data ? counts.data.approvals + counts.data.assignments + counts.data.reviews : 0;
  return items.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === '/'}
      className="nav-link"
      aria-disabled={item.soon || undefined}
      tabIndex={item.soon ? -1 : undefined}
    >
      <Icon name={item.icon} />
      <span className="label-full">{item.label}</span>
      <span className="label-short">{item.short ?? item.label}</span>
      {item.soon && <span className="badge soon">скоро</span>}
      {item.counter && waiting > 0 && <span className="count-pill">{waiting}</span>}
    </NavLink>
  ));
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const dark =
    theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  return (
    <button
      className="btn btn-ghost btn-icon"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      aria-label={dark ? 'Светлая тема' : 'Тёмная тема'}
      title={dark ? 'Светлая тема' : 'Тёмная тема'}
    >
      <Icon name={dark ? 'sun' : 'moon'} />
    </button>
  );
}

function Bell() {
  const unread = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
  const count = unread.data?.count ?? 0;
  // Как у мессенджеров: число непрочитанных на иконке установленного приложения.
  useEffect(() => setAppBadge(count), [count]);
  return (
    <Link to="/notifications" className="btn btn-ghost btn-icon bell" aria-label={`Уведомления: ${count} новых`}>
      <Icon name="bell" />
      {count > 0 && <span className="count">{count > 99 ? '99+' : count}</span>}
    </Link>
  );
}

/** Пришло push-уведомление — обновить колокольчик и списки; нажали на него — открыть задачу. */
function usePushEvents(signedIn: boolean) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (!signedIn) return;
    void syncPush();
    return onServiceWorkerMessage({
      notification: () => {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
        void qc.invalidateQueries({ queryKey: ['tasks'] });
      },
      open: (url) => {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
        void navigate(url);
      },
    });
  }, [signedIn, qc, navigate]);
}

export function AppLayout() {
  const { user, loading, can, logout } = useAuth();
  const location = useLocation();
  usePushEvents(Boolean(user));
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const admin = ADMIN.filter((i) => !i.permission || can(i.permission));
  return (
    <div className="shell">
      <nav className="rail" aria-label="Основное меню">
        <Link to="/" className="logo">
          <span className="logo-mark" aria-hidden>
            <Icon name="tasks" size={18} color="#fff" />
          </span>
          EDMS
        </Link>
        {/* Пункты меню прокручиваются, если не помещаются по высоте; логотип и карточка профиля на месте. */}
        <div className="rail-scroll">
          <NavItems items={MAIN} />
          {admin.length > 0 && (
            <>
              <div className="nav-group">Управление</div>
              <div className="desktop-only">
                <NavItems items={admin} />
              </div>
            </>
          )}
        </div>
        <NavLink to="/profile" className="nav-link mobile-only">
          <Icon name="user" />
          <span>Профиль</span>
        </NavLink>
        <div className="rail-foot">
          <NavLink to="/profile" className="nav-link">
            <Avatar name={user.fullName} size={28} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.fullName}
              </span>
              <span className="faint">{ROLE_LABELS[user.role]}</span>
            </span>
          </NavLink>
        </div>
      </nav>
      <div className="main">
        <header className="topbar">
          <TopBarSlot />
          <Bell />
          <ThemeToggle />
          <button className="btn btn-ghost btn-icon" onClick={() => void logout()} aria-label="Выйти" title="Выйти">
            <Icon name="logout" />
          </button>
        </header>
        <main className="page" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Закрывает страницу, если нет права. Сервер всё равно проверит право сам. */
export function RequirePermission({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(permission)) return <Navigate to="/" replace />;
  return children;
}
